import { mergeRefs, useRandomId, useForceUpdate, useOnMount, warnAboutMissingStyles } from '@entur/utils';
import classNames from 'classnames';
import * as React from 'react';
import React__default, { useRef } from 'react';
import { ValidationErrorFilledIcon, ValidationExclamationFilledIcon, ValidationSuccessFilledIcon, QuestionIcon, CheckIcon, CloseSmallIcon } from '@entur/icons';
import { SubLabel, Paragraph, Heading5, Label } from '@entur/typography';
import { Tooltip } from '@entur/tooltip';
import { colors } from '@entur/tokens';
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var _excluded$g = ["children", "hideIcon", "variant", "className"];
/** @deprecated use variant="information" instead */
var info = 'info';
/** @deprecated use variant="negative" instead */
var error$1 = 'error';
var AlertIcon = function AlertIcon(_ref) {
  var variant = _ref.variant;
  var iconClass = "eds-feedback-text__icon eds-feedback-text__icon--" + variant;
  switch (variant) {
    case 'success':
      return React__default.createElement(ValidationSuccessFilledIcon, {
        "aria-label": "Suksessmelding",
        className: iconClass
      });
    case 'negative':
      return React__default.createElement(ValidationErrorFilledIcon, {
        "aria-label": "Feilmelding",
        className: iconClass
      });
    case 'information':
      return null;
    case 'warning':
      return React__default.createElement(ValidationExclamationFilledIcon, {
        "aria-label": "Varselmelding",
        className: iconClass
      });
    case error$1:
      return React__default.createElement(ValidationErrorFilledIcon, {
        "aria-label": "Feilmelding",
        className: iconClass
      });
    case info:
      return null;
    default:
      return null;
  }
};
var FeedbackText = function FeedbackText(_ref2) {
  var children = _ref2.children,
    _ref2$hideIcon = _ref2.hideIcon,
    hideIcon = _ref2$hideIcon === void 0 ? false : _ref2$hideIcon,
    variant = _ref2.variant,
    className = _ref2.className,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded$g);
  return React__default.createElement(SubLabel, _extends({
    className: classNames('eds-feedback-text', {
      'eds-feedback-text--information': variant === info || variant === 'information'
    }, className)
  }, rest), !hideIcon && React__default.createElement(AlertIcon, {
    variant: variant
  }), React__default.createElement("span", {
    className: "eds-feedback-text__text"
  }, children));
};
var InputGroupContext = /*#__PURE__*/React.createContext({
  isFilled: false,
  setFilled: function setFilled() {
    return null;
  }
});
var InputGroupContextProvider = function InputGroupContextProvider(_ref) {
  var children = _ref.children;
  var _React$useState = React.useState(false),
    filled = _React$useState[0],
    setFilled = _React$useState[1];
  return React.createElement(InputGroupContext.Provider, {
    value: {
      isFilled: filled,
      setFilled: setFilled
    }
  }, children);
};
var useInputGroupContext = function useInputGroupContext() {
  return React.useContext(InputGroupContext);
};
var _excluded$f = ["label", "required", "labelId", "staticAnimation"];
var InputGroupLabel = function InputGroupLabel(_ref) {
  var label = _ref.label,
    required = _ref.required,
    labelId = _ref.labelId,
    _ref$staticAnimation = _ref.staticAnimation,
    staticAnimation = _ref$staticAnimation === void 0 ? false : _ref$staticAnimation,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$f);
  var _useInputGroupContext = useInputGroupContext(),
    isFilled = _useInputGroupContext.isFilled;
  var filler = staticAnimation || isFilled;
  return React__default.createElement("label", _extends({
    className: classNames(rest.className, {
      'eds-input-group-label-wrapper--filled': filler
    }),
    id: labelId
  }, rest), React__default.createElement("span", {
    className: classNames('eds-input-group__label', {
      'eds-input-group__label--filled': filler
    })
  }, label, " ", required && React__default.createElement("span", null, "*")));
};
var VariantContext = /*#__PURE__*/React__default.createContext(null);
var VariantProvider = function VariantProvider(_ref) {
  var children = _ref.children,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? null : _ref$variant;
  return React__default.createElement(VariantContext.Provider, {
    value: variant
  }, children);
};
var useVariant = function useVariant() {
  var context = React__default.useContext(VariantContext);
  return context;
};
var _excluded$e = ["children", "className", "disabled", "readOnly", "variant", "prepend", "append", "size", "isFilled", "label", "required", "labelTooltip", "feedback", "labelId", "labelProps", "style", "disableLabelAnimation", "ariaAlertOnFeedback"];
/** @deprecated use variant="negative" instead */
var error = 'error';
var BaseFormControl = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    variant = _ref.variant,
    prepend = _ref.prepend,
    append = _ref.append,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    isFilled = _ref.isFilled,
    label = _ref.label,
    required = _ref.required,
    labelTooltip = _ref.labelTooltip,
    feedback = _ref.feedback,
    labelId = _ref.labelId,
    labelProps = _ref.labelProps,
    style = _ref.style,
    _ref$disableLabelAnim = _ref.disableLabelAnimation,
    disableLabelAnimation = _ref$disableLabelAnim === void 0 ? false : _ref$disableLabelAnim,
    _ref$ariaAlertOnFeedb = _ref.ariaAlertOnFeedback,
    ariaAlertOnFeedback = _ref$ariaAlertOnFeedb === void 0 ? false : _ref$ariaAlertOnFeedb,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$e);
  var contextVariant = useVariant();
  var currentVariant = variant || contextVariant;
  return React__default.createElement(InputGroupContextProvider, null, React__default.createElement("div", null, React__default.createElement("div", _extends({
    className: classNames('eds-form-control-wrapper', className, "eds-form-control-wrapper--size-" + size, {
      'eds-form-control-wrapper--success': currentVariant === 'success',
      'eds-form-control-wrapper--negative': currentVariant === 'negative' || currentVariant === error,
      'eds-form-control-wrapper--disabled': disabled,
      'eds-form-control-wrapper--readonly': readOnly,
      'eds-form-control-wrapper--is-filled': isFilled
    }),
    ref: ref,
    style: style
  }, rest), prepend && React__default.createElement("div", {
    className: "eds-form-control__prepend"
  }, prepend), React__default.createElement(InputGroupLabel, _extends({
    label: label,
    required: required,
    labelId: labelId,
    staticAnimation: disableLabelAnimation
  }, labelProps)), children, append && React__default.createElement("div", {
    className: "eds-form-control__append"
  }, append), labelTooltip && React__default.createElement("div", {
    className: "eds-form-control__append eds-form-control__append--tooltip"
  }, React__default.createElement(Tooltip, {
    content: labelTooltip,
    placement: "right"
  }, React__default.createElement("span", {
    className: "eds-input-group__label-tooltip-icon"
  }, React__default.createElement(QuestionIcon, null))))), feedback && currentVariant && React__default.createElement(FeedbackText, {
    variant: currentVariant,
    role: ariaAlertOnFeedback ? 'alert' : undefined
  }, feedback)));
});
var _excluded$d = ["checked", "className", "children", "style", "disabled", "reduceClickArea"];
var Checkbox = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var checked = _ref.checked,
    className = _ref.className,
    children = _ref.children,
    style = _ref.style,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    reduceClickArea = _ref.reduceClickArea,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$d);
  var inputRef = React__default.useRef(null);
  var isIndeterminate = checked === 'indeterminate';
  var isControlled = checked !== undefined;
  React__default.useEffect(function () {
    if (inputRef && inputRef.current) {
      inputRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);
  return React__default.createElement("label", {
    className: classNames('eds-checkbox__container', className, {
      'eds-checkbox--disabled': disabled,
      'eds-checkbox__container--reduced-click-area': reduceClickArea
    }),
    style: style
  }, React__default.createElement("input", _extends({
    type: "checkbox",
    ref: mergeRefs(ref, inputRef),
    checked: isControlled ? checked === true : undefined,
    disabled: disabled
  }, rest)), React__default.createElement("span", {
    className: classNames('eds-checkbox__icon', {
      'eds-checkbox__icon--disabled': disabled,
      'eds-checkbox__icon--reduced-click-area': reduceClickArea
    })
  }, React__default.createElement(CheckboxIcon, {
    indeterminate: isIndeterminate
  })), children && React__default.createElement(Paragraph, {
    className: "eds-checkbox__label",
    margin: "none",
    as: "span"
  }, children));
});
var CheckboxIcon = function CheckboxIcon(_ref2) {
  var _ref2$indeterminate = _ref2.indeterminate,
    indeterminate = _ref2$indeterminate === void 0 ? false : _ref2$indeterminate;
  return React__default.createElement("svg", {
    className: "eds-checkbox-icon",
    width: "11px",
    height: "9px",
    viewBox: "6 11 37 33",
    "aria-hidden": true
  }, indeterminate ? React__default.createElement("rect", {
    x: "10",
    y: "25",
    width: "28",
    height: "5",
    fill: "white"
  }) : React__default.createElement("path", {
    d: "M14.1 27.2l7.1 7.2 14.6-14.8",
    fill: "none"
  }));
};
var _excluded$c = ["children", "className", "label"];
var Fieldset = function Fieldset(_ref) {
  var children = _ref.children,
    className = _ref.className,
    label = _ref.label,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$c);
  return React__default.createElement("fieldset", _extends({
    className: classNames('eds-fieldset', className)
  }, rest), label && React__default.createElement(Heading5, {
    as: "legend"
  }, label), children);
};
var RadioGroupContext = /*#__PURE__*/React__default.createContext(null);
var RadioGroupContextProvider = RadioGroupContext.Provider;
var useRadioGroupContext = function useRadioGroupContext() {
  var context = React__default.useContext(RadioGroupContext);
  if (!context) {
    throw new Error('You need to wrap your RadioButtons in a RadioGroup component');
  }
  return context;
};
var _excluded$b = ["className", "children", "value", "disabled"];
var Radio = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var _rest$name, _rest$checked, _rest$onChange;
  var className = _ref.className,
    children = _ref.children,
    value = _ref.value,
    disabled = _ref.disabled,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$b);
  var classList = classNames(className, 'eds-form-component--radio__radio', {
    'eds-form-component--radio__radio--disabled': disabled
  });
  var _useRadioGroupContext = useRadioGroupContext(),
    name = _useRadioGroupContext.name,
    selectedValue = _useRadioGroupContext.value,
    onChange = _useRadioGroupContext.onChange;
  return React__default.createElement("label", {
    className: "eds-form-component--radio__container"
  }, React__default.createElement("input", _extends({
    type: "radio",
    name: (_rest$name = rest.name) != null ? _rest$name : name,
    ref: ref,
    value: value,
    checked: (_rest$checked = rest.checked) != null ? _rest$checked : selectedValue === value,
    onChange: (_rest$onChange = rest.onChange) != null ? _rest$onChange : onChange,
    disabled: disabled
  }, rest)), React__default.createElement("span", {
    className: classList
  }, React__default.createElement("span", {
    className: "eds-form-component--radio__circle"
  })), children && React__default.createElement(Paragraph, {
    margin: "none",
    as: "span",
    className: "eds-form-component--radio__label"
  }, children));
});
var _excluded$a = ["className", "children", "value", "title", "secondaryLabel", "size", "hideSelectionIndicator", "style", "id", "disabled", "type", "onChange", "checked", "name"];
var InputPanelBase = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var _ref2, _inputRef$current, _ref3, _inputRef$current2;
  var className = _ref.className,
    children = _ref.children,
    value = _ref.value,
    title = _ref.title,
    secondaryLabel = _ref.secondaryLabel,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    _ref$hideSelectionInd = _ref.hideSelectionIndicator,
    hideSelectionIndicator = _ref$hideSelectionInd === void 0 ? false : _ref$hideSelectionInd,
    style = _ref.style,
    id = _ref.id,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'radio' : _ref$type,
    onChange = _ref.onChange,
    checked = _ref.checked,
    name = _ref.name,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$a);
  var classList = classNames(className, 'eds-input-panel__container', "eds-input-panel--" + size);
  var inputRef = useRef(null);
  var defaultId = useRandomId('eds-inputpanel');
  var inputPanelId = id || defaultId;
  var forceUpdate = useForceUpdate();
  var handleOnChange = function handleOnChange(e) {
    if (onChange === undefined) forceUpdate();
    onChange == null ? void 0 : onChange(e);
  };
  return React__default.createElement("label", {
    className: "eds-input-panel",
    htmlFor: inputPanelId
  }, React__default.createElement("input", _extends({
    type: type,
    name: name,
    ref: mergeRefs(ref, inputRef),
    value: value,
    checked: checked,
    onChange: handleOnChange,
    id: inputPanelId,
    disabled: disabled
  }, rest)), React__default.createElement("div", {
    className: classList,
    style: style
  }, React__default.createElement("div", {
    className: "eds-input-panel__title-wrapper"
  }, React__default.createElement("div", {
    className: "eds-input-panel__title"
  }, title), React__default.createElement("div", {
    className: "eds-input-panel__secondary-label-and-icon-wrapper"
  }, secondaryLabel !== undefined && React__default.createElement(React__default.Fragment, null, secondaryLabel), React__default.createElement("span", {
    style: {
      pointerEvents: 'none'
    }
  }, !(disabled || hideSelectionIndicator) && (type === 'radio' ? React__default.createElement(Radio, {
    name: "",
    value: "",
    checked: (_ref2 = checked != null ? checked : (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.checked) != null ? _ref2 : false,
    onChange: function onChange() {
      return;
    },
    "aria-hidden": "true",
    tabIndex: -1
  }) : React__default.createElement(Checkbox, {
    checked: (_ref3 = checked != null ? checked : (_inputRef$current2 = inputRef.current) == null ? void 0 : _inputRef$current2.checked) != null ? _ref3 : false,
    onChange: function onChange() {
      return null;
    },
    "aria-hidden": "true",
    tabIndex: -1
  }))))), children && React__default.createElement("div", {
    className: "eds-input-panel__additional-content"
  }, children)));
});
var _excluded$9 = ["className", "children", "value", "title", "secondaryLabel", "size", "hideRadioButton", "style", "id", "disabled"];
var RadioPanel = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    children = _ref.children,
    value = _ref.value,
    title = _ref.title,
    secondaryLabel = _ref.secondaryLabel,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    _ref$hideRadioButton = _ref.hideRadioButton,
    hideRadioButton = _ref$hideRadioButton === void 0 ? false : _ref$hideRadioButton,
    style = _ref.style,
    id = _ref.id,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$9);
  var _useRadioGroupContext = useRadioGroupContext(),
    name = _useRadioGroupContext.name,
    selected = _useRadioGroupContext.value,
    onChange = _useRadioGroupContext.onChange;
  return React__default.createElement(InputPanelBase, _extends({
    type: "radio",
    name: name,
    title: title,
    value: value,
    checked: selected === value,
    onChange: onChange,
    className: className,
    secondaryLabel: secondaryLabel,
    size: size,
    hideSelectionIndicator: hideRadioButton,
    style: style,
    id: id,
    disabled: disabled
  }, rest, {
    ref: ref
  }), children);
});
var _excluded$8 = ["name", "checked", "onChange", "className", "children", "value", "title", "secondaryLabel", "size", "hideCheckbox", "style", "id", "disabled"];
var CheckboxPanel = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var name = _ref.name,
    checked = _ref.checked,
    onChange = _ref.onChange,
    className = _ref.className,
    children = _ref.children,
    value = _ref.value,
    title = _ref.title,
    secondaryLabel = _ref.secondaryLabel,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    _ref$hideCheckbox = _ref.hideCheckbox,
    hideCheckbox = _ref$hideCheckbox === void 0 ? false : _ref$hideCheckbox,
    style = _ref.style,
    id = _ref.id,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$8);
  return React__default.createElement(InputPanelBase, _extends({
    type: "checkbox",
    name: name,
    title: title,
    value: value,
    checked: checked,
    onChange: onChange,
    className: className,
    secondaryLabel: secondaryLabel,
    size: size,
    hideSelectionIndicator: hideCheckbox,
    style: style,
    id: id,
    disabled: disabled
  }, rest, {
    ref: ref
  }), children);
});
var _excluded$7 = ["name", "value", "children", "onChange", "label"];
var RadioGroup = function RadioGroup(_ref) {
  var name = _ref.name,
    value = _ref.value,
    children = _ref.children,
    onChange = _ref.onChange,
    label = _ref.label,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$7);
  var contextValue = React__default.useMemo(function () {
    return {
      name: name,
      value: value,
      onChange: onChange
    };
  }, [name, value, onChange]);
  return React__default.createElement(RadioGroupContextProvider, {
    value: contextValue
  }, label ? React__default.createElement(Fieldset, _extends({
    label: label
  }, rest), children) : children);
};
var _excluded$6 = ["className", "children", "labelPlacement", "icon", "hideIcon", "color", "contrastColor", "size", "checked"];
var Switch = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    children = _ref.children,
    _ref$labelPlacement = _ref.labelPlacement,
    labelPlacement = _ref$labelPlacement === void 0 ? 'right' : _ref$labelPlacement,
    icon = _ref.icon,
    _ref$hideIcon = _ref.hideIcon,
    hideIcon = _ref$hideIcon === void 0 ? false : _ref$hideIcon,
    _ref$color = _ref.color,
    color = _ref$color === void 0 ? colors.validation.mint : _ref$color,
    _ref$contrastColor = _ref.contrastColor,
    contrastColor = _ref$contrastColor === void 0 ? colors.validation.mintContrast : _ref$contrastColor,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    checked = _ref.checked,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$6);
  var displayedIcon = function displayedIcon() {
    if (icon) return icon;
    if (checked === undefined) return React__default.createElement(React__default.Fragment, null);
    var iconSize = size === 'large' ? 23 : undefined;
    return checked ? React__default.createElement(CheckIcon, {
      size: iconSize
    }) : React__default.createElement(CloseSmallIcon, {
      size: iconSize
    });
  };
  return React__default.createElement("label", {
    className: classNames('eds-switch', "eds-switch--" + labelPlacement, className),
    style: _extends({}, rest.style)
  }, React__default.createElement("input", _extends({
    type: "checkbox",
    ref: ref,
    checked: checked
  }, rest)), React__default.createElement("span", {
    className: classNames('eds-switch__switch', "eds-switch__switch--" + size),
    style: {
      '--eds-switch-color': color,
      '--eds-switch-contrast-color': contrastColor
    },
    "aria-hidden": "true"
  }, React__default.createElement("span", {
    className: "eds-switch__circle"
  }, !hideIcon && displayedIcon())), children && React__default.createElement("span", {
    className: classNames('eds-switch__label', "eds-switch__label--" + size + "--" + labelPlacement)
  }, children));
});

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- disabled during Yarn upgrade
function hasValue(value) {
  return value != null && !(Array.isArray(value) && value.length === 0);
}
// Determine if field is empty or filled.
// Response determines if label is presented above field or as placeholder.
//
// @param obj
// @param SSR
// @returns {boolean} False when not present or empty string.
//                    True when any number or string with length.
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- disabled during Yarn upgrade
function isFilled(obj, SSR) {
  if (SSR === void 0) {
    SSR = false;
  }
  return obj && (hasValue(obj.value) && obj.value !== '' || SSR && hasValue(obj.defaultValue) && obj.defaultValue !== '');
}
var _excluded$5 = ["variant", "disabled", "readOnly", "className", "style", "label", "feedback", "labelTooltip", "onChange", "disableLabelAnimation"],
  _excluded2$1 = ["readOnly", "disabled", "onChange", "value", "variant"];
var TextArea = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var variant = _ref.variant,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    className = _ref.className,
    style = _ref.style,
    label = _ref.label,
    feedback = _ref.feedback,
    labelTooltip = _ref.labelTooltip,
    onChange = _ref.onChange,
    disableLabelAnimation = _ref.disableLabelAnimation,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$5);
  var textAreaId = useRandomId('eds-textarea');
  return React__default.createElement(BaseFormControl, {
    className: className,
    disabled: disabled,
    readOnly: readOnly,
    variant: variant,
    style: style,
    label: label,
    labelId: textAreaId,
    feedback: feedback,
    labelTooltip: labelTooltip,
    labelProps: {
      className: 'eds-textarea__label'
    },
    disableLabelAnimation: disableLabelAnimation
  }, React__default.createElement(TextAreaBase, _extends({
    readOnly: readOnly,
    disabled: disabled,
    ref: ref,
    "aria-labelledby": textAreaId,
    onChange: onChange,
    variant: variant
  }, rest)));
});
var TextAreaBase = /*#__PURE__*/React__default.forwardRef(function (_ref2, ref) {
  var readOnly = _ref2.readOnly,
    disabled = _ref2.disabled,
    onChange = _ref2.onChange,
    value = _ref2.value,
    variant = _ref2.variant,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2$1);
  var contextVariant = useVariant();
  var currentVariant = variant || contextVariant;
  var _useInputGroupContext = useInputGroupContext(),
    isInputFilled = _useInputGroupContext.isFilled,
    setFiller = _useInputGroupContext.setFilled;
  useOnMount(function () {
    if (value != null && value.toString() || rest.defaultValue) {
      setFiller && !isInputFilled && setFiller(true);
    }
  });
  React__default.useEffect(function () {
    if (value != null && value.toString() && setFiller && !isInputFilled) {
      setFiller(true);
    }
  }, [value, setFiller, isInputFilled]);
  var handleChange = function handleChange(event) {
    if (isFilled(event.target)) {
      setFiller && !isInputFilled && setFiller(true);
    } else {
      setFiller && isInputFilled && setFiller(false);
    }
    if (onChange) {
      onChange(event);
    }
  };
  return React__default.createElement("textarea", _extends({
    className: "eds-form-control eds-textarea",
    ref: ref,
    readOnly: readOnly,
    disabled: disabled,
    onChange: handleChange,
    value: value,
    "aria-invalid": currentVariant === 'error'
  }, rest));
});
var _excluded$4 = ["prepend", "append", "variant", "disabled", "readOnly", "className", "style", "size", "label", "required", "labelTooltip", "feedback", "onChange", "disableLabelAnimation", "labelProps", "clearable", "onClear", "value", "ariaAlertOnFeedback"],
  _excluded2 = ["disabled", "readOnly", "placeholder", "onChange", "value", "variant"],
  _excluded3 = ["onClear"];
var TextField = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var prepend = _ref.prepend,
    append = _ref.append,
    variant = _ref.variant,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$readOnly = _ref.readOnly,
    readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly,
    className = _ref.className,
    style = _ref.style,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    label = _ref.label,
    required = _ref.required,
    labelTooltip = _ref.labelTooltip,
    feedback = _ref.feedback,
    onChange = _ref.onChange,
    disableLabelAnimation = _ref.disableLabelAnimation,
    labelProps = _ref.labelProps,
    _ref$clearable = _ref.clearable,
    clearable = _ref$clearable === void 0 ? false : _ref$clearable,
    onClear = _ref.onClear,
    value = _ref.value,
    _ref$ariaAlertOnFeedb = _ref.ariaAlertOnFeedback,
    ariaAlertOnFeedback = _ref$ariaAlertOnFeedb === void 0 ? false : _ref$ariaAlertOnFeedb,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$4);
  var randomId = useRandomId('eds-textfield');
  var textFieldId = labelProps && labelProps.id ? labelProps.id : randomId;
  return React__default.createElement(BaseFormControl, {
    disabled: disabled,
    readOnly: readOnly,
    variant: variant,
    prepend: prepend,
    append: clearable && onClear ? React__default.createElement(ClearButton, {
      onClear: onClear
    }) : append,
    className: className,
    style: style,
    size: size,
    label: label,
    required: required,
    labelTooltip: labelTooltip,
    labelId: textFieldId,
    feedback: feedback,
    disableLabelAnimation: disableLabelAnimation,
    labelProps: labelProps,
    ariaAlertOnFeedback: ariaAlertOnFeedback
  }, React__default.createElement(TextFieldBase, _extends({
    disabled: disabled,
    readOnly: readOnly,
    ref: ref,
    "aria-labelledby": textFieldId,
    onChange: onChange,
    value: value,
    variant: variant
  }, rest)));
});
var TextFieldBase = /*#__PURE__*/React__default.forwardRef(function (_ref2, forwardRef) {
  var disabled = _ref2.disabled,
    readOnly = _ref2.readOnly,
    placeholder = _ref2.placeholder,
    onChange = _ref2.onChange,
    value = _ref2.value,
    variant = _ref2.variant,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  var contextVariant = useVariant();
  var currentVariant = variant || contextVariant;
  var _useInputGroupContext = useInputGroupContext(),
    isInputFilled = _useInputGroupContext.isFilled,
    setFiller = _useInputGroupContext.setFilled;
  useOnMount(function () {
    if (value != null && value.toString() || rest.defaultValue) {
      setFiller && !isInputFilled && setFiller(true);
    }
  });
  React__default.useEffect(function () {
    if (value != null && value.toString() && setFiller && !isInputFilled) {
      setFiller(true);
    }
  }, [value, setFiller, isInputFilled]);
  var handleChange = function handleChange(event) {
    if (isFilled(event.target)) {
      setFiller && !isInputFilled && setFiller(true);
    } else {
      setFiller && isInputFilled && setFiller(false);
    }
    if (onChange) {
      onChange(event);
    }
  };
  return React__default.createElement("input", _extends({
    "aria-invalid": currentVariant === 'error',
    className: "eds-form-control",
    disabled: disabled,
    readOnly: readOnly,
    ref: forwardRef,
    placeholder: placeholder,
    onChange: handleChange,
    value: value
  }, rest));
});
var ClearButton = function ClearButton(_ref3) {
  var onClear = _ref3.onClear,
    props = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  var _useInputGroupContext2 = useInputGroupContext(),
    hasValue = _useInputGroupContext2.isFilled,
    setFilled = _useInputGroupContext2.setFilled;
  return React__default.createElement("div", {
    className: "eds-textfield__clear-button-wrapper"
  }, hasValue && React__default.createElement("div", {
    className: "eds-textfield__divider"
  }), hasValue && React__default.createElement("button", _extends({
    className: "eds-textfield__clear-button",
    type: "button",
    tabIndex: -1,
    onClick: function onClick() {
      setFilled(false);
      onClear();
    }
  }, props), React__default.createElement(CloseSmallIcon, null)));
};
var _excluded$3 = ["name", "onChange", "selectedValue", "multiple", "size"];
var SegmentedContext = /*#__PURE__*/React__default.createContext(null);
var SegmentedProvider = function SegmentedProvider(_ref) {
  var name = _ref.name,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? function () {
      return undefined;
    } : _ref$onChange,
    selectedValue = _ref.selectedValue,
    multiple = _ref.multiple,
    size = _ref.size,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$3);
  var generatedName = useRandomId('eds-segmented-control');
  var contextValue = React__default.useMemo(function () {
    return {
      name: name || generatedName,
      onChange: onChange,
      multiple: multiple,
      selectedValue: selectedValue,
      size: size
    };
  }, [generatedName, multiple, name, onChange, selectedValue, size]);
  return React__default.createElement(SegmentedContext.Provider, _extends({
    value: contextValue
  }, rest));
};
var useSegmentedContext = function useSegmentedContext() {
  var context = React__default.useContext(SegmentedContext);
  if (!context) {
    throw new Error('You need to wrap your SegmentedChoice in either SegmentedControl or MultipleSegmentedControl');
  }
  return context;
};
var _excluded$2 = ["children", "className", "style", "value", "name", "onChange"];
var SegmentedChoice = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    style = _ref.style,
    value = _ref.value,
    name = _ref.name,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? function () {
      return undefined;
    } : _ref$onChange,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  var _useSegmentedContext = useSegmentedContext(),
    commonName = _useSegmentedContext.name,
    selectedValue = _useSegmentedContext.selectedValue,
    commonOnChange = _useSegmentedContext.onChange,
    multiple = _useSegmentedContext.multiple,
    size = _useSegmentedContext.size;
  var isChecked = multiple ? selectedValue[value] : selectedValue === value;
  var handleChange = function handleChange(e) {
    onChange(e);
    if (multiple) {
      var _extends2;
      commonOnChange(_extends({}, selectedValue, (_extends2 = {}, _extends2[value] = e.target.checked, _extends2)));
    } else if (e.target.checked) {
      commonOnChange(value);
    }
  };
  return React__default.createElement("label", {
    className: classNames('eds-segmented-choice', className),
    style: style
  }, React__default.createElement("input", _extends({
    type: multiple ? 'checkbox' : 'radio',
    name: name || commonName,
    checked: isChecked,
    value: value,
    onChange: handleChange,
    ref: ref
  }, rest)), React__default.createElement("div", {
    className: classNames('eds-base-segmented', {
      'eds-base-segmented--large': size === 'large'
    })
  }, children));
});
var _excluded$1 = ["children", "label", "name", "onChange", "selectedValue", "size", "className"];
var SegmentedControl = function SegmentedControl(_ref) {
  var children = _ref.children,
    label = _ref.label,
    name = _ref.name,
    onChange = _ref.onChange,
    selectedValue = _ref.selectedValue,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    className = _ref.className,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  return React__default.createElement(SegmentedProvider, {
    name: name,
    selectedValue: selectedValue,
    onChange: onChange,
    multiple: false,
    size: size
  }, React__default.createElement(Fieldset, {
    label: label
  }, React__default.createElement("div", _extends({
    className: classNames('eds-segmented-control', className)
  }, rest), children)));
};
var _excluded = ["children", "label", "name", "onChange", "selectedValue"];
/**This component is not used by anyone, and is therefore deprecated
 * @deprecated
 */
var MultipleSegmentedControl = function MultipleSegmentedControl(_ref) {
  var children = _ref.children,
    label = _ref.label,
    name = _ref.name,
    onChange = _ref.onChange,
    selectedValue = _ref.selectedValue,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return React__default.createElement(SegmentedProvider, {
    name: name,
    selectedValue: selectedValue,
    onChange: onChange,
    multiple: true,
    size: "medium"
  }, React__default.createElement(Label, {
    as: "div"
  }, label), React__default.createElement("div", _extends({
    className: "eds-segmented-control"
  }, rest), children));
};
warnAboutMissingStyles('form', 'icons', 'typography');
export { BaseFormControl, Checkbox, CheckboxPanel, FeedbackText, Fieldset, InputGroupContextProvider, InputGroupLabel, MultipleSegmentedControl, Radio, RadioGroup, RadioPanel, SegmentedChoice, SegmentedControl, Switch, TextArea, TextField, VariantProvider, hasValue, isFilled, useInputGroupContext, useVariant };